import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import heroImage  from '../assets/craftOnline.jpg';
import heroBottom from '../assets/hero-bottom.svg';
import CoverFlow  from '../components/CoverFlow.js';
import c2         from '../assets/c2.png';
import c3         from '../assets/c3.png';
import bgImage    from '../assets/background.png';
import mechanicImage from "../assets/mechanics.jpeg";
import pipersImage from "../assets/pipers.jpg";
import client1    from '../assets/68.png';
import client2    from '../assets/20.png';
import client3    from '../assets/33.png';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import AOS from 'aos';
import 'aos/dist/aos.css';
import RaiseIssueForm from '../components/RaiseIssueForm';

const API_BASE_URL     = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api';
const MEDIA_URL        = process.env.REACT_APP_MEDIA_URL    || 'https://staging.kaakazini.com';
const FALLBACK_AVATARS = [client1, client2, client3];

const imgUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${MEDIA_URL}${path}`;
};

const PLACEHOLDER = 'https://placehold.co/400x220/fff000/000000?text=No+Image';
const SERVICE_CATEGORIES = [
  { key:'home',        label:'Home Services',                keywords:['clean','domestic','nanny','maid','laundry'] },
  { key:'commercial',  label:'Commercial Services',           keywords:['office','commercial','security','janitorial'] },
  { key:'construction',label:'Construction',                  keywords:['mason','construction','builder','roofing','steel','welding'] },
  { key:'repairs',     label:'Repairs & Maintenance',         keywords:['plumb','electric','repair','maintenance','appliance','hvac'] },
  { key:'finishing',   label:'Interior & Exterior Finishing', keywords:['paint','tiling','tile','plaster','interior','decor'] },
  { key:'outdoor',     label:'Outdoor Services',              keywords:['garden','landscap','fence','outdoor'] },
  { key:'tech',        label:'Technology Services',           keywords:['it ','tech','computer','network','cctv','solar','electronic'] },
  { key:'automotive',  label:'Automotive Services',           keywords:['mechanic','auto','car','vehicle'] },
  { key:'furniture',   label:'Furniture & Woodwork',          keywords:['carpen','furniture','wood'] },
  { key:'craft',       label:'Custom Craftsmanship',          keywords:['tailor','craft','custom','art'] },
];

function categorizeService(name) {
  const n = (name || '').toLowerCase();
  for (const cat of SERVICE_CATEGORIES) {
    if (cat.keywords.some(k => n.includes(k))) return cat.key;
  }
  return 'other';
}

const getCover = (c) => imgUrl(
  c?.gallery_images?.[0]?.image_url ||
  c?.services?.[0]?.image_url       ||
  c?.services?.[0]?.image           ||
  c?.service_image                   ||
  null
);

const getAvatar = (c) => imgUrl(
  c?.profile_url   ||
  c?.profile       ||
  c?.profile_image ||
  c?.avatar        ||
  null
);

function StarRating({ rating }) {
  const n = Number(rating) || 0;
  const full = Math.floor(n), half = n % 1 >= 0.5, empty = 5 - full - (half ? 1 : 0);
  return (
    <span className="kk-stars" aria-label={`${n} stars`}>
      {[...Array(full)].map((_,i)  => <i key={`f${i}`} className="fas fa-star"/>)}
      {half && <i className="fas fa-star-half-alt"/>}
      {[...Array(empty)].map((_,i) => <i key={`e${i}`} className="far fa-star"/>)}
    </span>
  );
}

function SkeletonCard() {
  return (
    <div className="col">
      <div style={{background:'#fff',borderRadius:14,overflow:'hidden',opacity:.7,border:'1.5px solid #eee'}}>
        <div className="kk-skel" style={{height:200}}/>
        <div style={{padding:16,display:'flex',flexDirection:'column',gap:10}}>
          <div style={{display:'flex',gap:10,alignItems:'center'}}>
            <div className="kk-skel" style={{width:42,height:42,borderRadius:'50%',flexShrink:0}}/>
            <div style={{flex:1}}>
              <div className="kk-skel" style={{height:11,width:'65%',borderRadius:6}}/>
              <div className="kk-skel" style={{height:9,width:'40%',borderRadius:6,marginTop:6}}/>
            </div>
          </div>
          <div className="kk-skel" style={{height:9,borderRadius:6}}/>
          <div className="kk-skel" style={{height:36,borderRadius:10,marginTop:4}}/>
        </div>
      </div>
    </div>
  );
}

function CraftsmanCard({ service }) {
  const cover  = getCover(service);
  const avatar = getAvatar(service);
  const id     = service.slug || service.id || '';
  const rating = Number(service.average_rating) || 0;
  const name   = service.full_name || service.name || 'Service Provider';
  return (
    <div className="kk-card">
      <div className="kk-card-cover">
        <img src={cover || PLACEHOLDER} alt={name} className="kk-card-img" loading="lazy"
          onError={e=>{ e.target.src=PLACEHOLDER; }}/>
        <span className="kk-badge-trade">{service.primary_service}</span>
        {rating >= 4.5 && <span className="kk-badge-top">⭐ Top Rated</span>}
      </div>
      <div className="kk-card-body">
        <div className="kk-card-profile">
          {avatar
            ? <img src={avatar} alt={name} className="kk-avatar" loading="lazy"
                onError={e=>{ e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=fff000&color=000000&size=88`; }}/>
            : <img src={`https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=fff000&color=000000&size=88`}
                alt={name} className="kk-avatar" loading="lazy"/>
          }
          <div style={{minWidth:0}}>
            <p className="kk-card-name">{name}</p>
            {service.location && (
              <span className="kk-card-loc">
                <i className="fas fa-map-marker-alt me-1"/>{service.location}
              </span>
            )}
          </div>
        </div>
        {rating > 0 && (
          <div style={{display:'flex',alignItems:'center',gap:6}}>
            <StarRating rating={rating}/>
            <span style={{fontSize:'.75rem',color:'#6c757d',fontWeight:600}}>{rating.toFixed(1)}</span>
          </div>
        )}
        {service.description && (
          <p className="kk-card-desc">
            {service.description.length > 80 ? service.description.slice(0,80)+'…' : service.description}
          </p>
        )}
        {id
          ? <Link to={`/craftsmen/${id}`} className="kk-btn-hire-sm">View Profile &amp; Hire</Link>
          : <span className="kk-btn-hire-sm kk-btn-muted">Unavailable</span>
        }
      </div>
    </div>
  );
}

function ServiceModal({ isOpen, onClose, serviceName, craftsmen, coverImg, navigate, user }) {
  const panelRef = useRef(null);
  useEffect(() => { document.body.style.overflow = isOpen ? 'hidden' : ''; return () => { document.body.style.overflow = ''; }; }, [isOpen]);
  useEffect(() => {
    const fn = e => { if (e.key === 'Escape') onClose(); };
    if (isOpen) window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [isOpen, onClose]);
  if (!isOpen) return null;

  const hireDestination = user?.role === 'client' ? '/hire' : '/HireLogin';

  return (
    <div className="kk-modal-backdrop" onClick={e=>{ if(panelRef.current && !panelRef.current.contains(e.target)) onClose(); }}
      role="dialog" aria-modal="true" aria-label={`${serviceName} providers`}>
      <div className="kk-modal-panel" ref={panelRef}>
        <div className="kk-modal-hero">
          <img src={coverImg || PLACEHOLDER} alt={serviceName} className="kk-modal-hero-img"
            onError={e=>{e.target.src=PLACEHOLDER;}}/>
          <div className="kk-modal-hero-overlay"/>
          <div className="kk-modal-hero-content">
            <span className="kk-modal-badge"><i className="fas fa-hard-hat me-2"/>{serviceName}</span>
            <h2 className="kk-modal-title">{serviceName} Service Providers Near You</h2>
            <p className="kk-modal-sub">{craftsmen.length} verified professional{craftsmen.length!==1?'s':''} available</p>
          </div>
          <button className="kk-modal-close" onClick={onClose} aria-label="Close">
            <i className="fas fa-times"/>
          </button>
        </div>
        <div className="kk-modal-body">
          {craftsmen.length === 0 ? (
            <div style={{textAlign:'center',padding:'48px 20px',color:'#6c757d'}}>
              <div style={{fontSize:'2.5rem',marginBottom:12}}>🔍</div>
              <p style={{fontWeight:600}}>No service providers available here yet — check back soon.</p>
            </div>
          ) : (
            <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-4">
              {craftsmen.map((c, idx) => {
                const src = getCover(c) || PLACEHOLDER;
                const av  = getAvatar(c);
                const cId = c.slug || c.id || '';
                const nm  = c.full_name || c.name || 'Service Provider';
                const rt  = Number(c.average_rating) || 0;
                const getLabel = sv => typeof sv==='string' ? sv : sv?.name||sv?.title||'';
                return (
                  <div key={c.id||idx} className="col">
                    <div className="kk-modal-card">
                      <div className="kk-modal-card-cover">
                        <img src={src} alt={c.primary_service} loading="lazy" className="kk-modal-card-img"
                          onError={e=>{e.target.src=PLACEHOLDER;}}/>
                        {rt>=4.5 && <span className="kk-badge-top" style={{top:8,right:8}}>⭐ Top Rated</span>}
                      </div>
                      <div className="kk-modal-card-body">
                        <div className="kk-card-profile">
                          {av
                            ? <img src={av} alt={nm} className="kk-avatar" loading="lazy"
                                onError={e=>{ e.target.src=`https://ui-avatars.com/api/?name=${encodeURIComponent(nm)}&background=fff000&color=000000&size=88`; }}/>
                            : <img src={`https://ui-avatars.com/api/?name=${encodeURIComponent(nm)}&background=fff000&color=000000&size=88`}
                                alt={nm} className="kk-avatar" loading="lazy"/>
                          }
                          <div style={{minWidth:0}}>
                            <p className="kk-card-name">{nm}</p>
                            {c.location && <span className="kk-card-loc"><i className="fas fa-map-marker-alt me-1"/>{c.location}</span>}
                          </div>
                        </div>
                        {rt>0 && <div style={{display:'flex',alignItems:'center',gap:5,marginTop:4}}><StarRating rating={rt}/><span style={{fontSize:'.72rem',color:'#6c757d',fontWeight:600}}>{rt.toFixed(1)}</span></div>}
                        {c.description && <p className="kk-card-desc">{c.description.length>90?c.description.slice(0,90)+'…':c.description}</p>}
                        {c.services?.length>0 && (
                          <div style={{display:'flex',flexWrap:'wrap',gap:4,marginTop:4}}>
                            {c.services.slice(0,3).map((sv,si)=>{ const l=getLabel(sv); return l?<span key={si} className="kk-sub-tag">{l}</span>:null; })}
                          </div>
                        )}
                        {c.phone && (
                          <a href={`tel:${c.phone}`} className="kk-phone-pill" onClick={e=>e.stopPropagation()}>
                            <i className="fas fa-phone me-1"/>{c.phone}
                          </a>
                        )}
                        {cId ? (
                          <div style={{display:'flex',flexWrap:'wrap',gap:7,marginTop:'auto'}}>
                            <button className="kk-modal-btn-outline" onClick={()=>{ onClose(); setTimeout(()=>navigate(`/craftsmen/${cId}`),50); }}>
                              <i className="fas fa-user me-1"/>Profile
                            </button>
                            <button className="kk-modal-btn-hire" onClick={()=>{ onClose(); setTimeout(()=>navigate(hireDestination),50); }}>
                              <i className="fas fa-hard-hat me-1"/>Hire {nm.split(' ')[0]}
                            </button>
                          </div>
                        ) : <span className="kk-btn-hire-sm kk-btn-muted">Unavailable</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Count-up utilities for the Stats section ──
function useCountUp(target, { duration = 1400, start = false } = {}) {
  const [value, setValue] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!start || typeof target !== 'number' || isNaN(target)) return;
    const from = 0;
    const to = target;
    const startTime = performance.now();

    const tick = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setValue(Math.round(from + (to - from) * eased));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setValue(to);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [target, start, duration]);

  return value;
}

function useInView(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
}

function StatCounter({ target, label, ready }) {
  const ref = useRef(null);
  const inView = useInView(ref);
  const shouldCount = ready && inView && typeof target === 'number';
  const count = useCountUp(target, { start: shouldCount });
  return (
    <div className="kk-stat-flat" ref={ref}>
      <h3 className="kk-stat-flat-value">
        {ready ? (typeof target === 'number' ? count : target) : '—'}
      </h3>
      <p className="kk-stat-flat-label">{label}</p>
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [craftsmen,      setCraftsmen]      = useState([]);
  const [loadingCraft,   setLoadingCraft]   = useState(true);
  const [query,          setQuery]          = useState('');
  const [searched,       setSearched]       = useState(false);
  const [results,        setResults]        = useState([]);
  const [reviews,        setReviews]        = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [reviewsError,   setReviewsError]   = useState(false);
  const [showTop, setShowTop] = useState(false);
  const [modalOpen,    setModalOpen]    = useState(false);
  const [modalSvcName, setModalSvcName] = useState('');
  const [modalList,    setModalList]    = useState([]);
  const [modalCover,   setModalCover]   = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const resultsRef = useRef(null);

  useEffect(() => { const fn = ()=>setShowTop(window.scrollY>500); window.addEventListener('scroll',fn,{passive:true}); return ()=>window.removeEventListener('scroll',fn); },[]);
  useEffect(() => { AOS.init({ duration:700, once:true, offset:40 }); },[]);

  // FIXED: approved providers are shown even when primary_service is empty
  // (their trade/profession is used instead), so services no longer disappear.
  useEffect(() => {
    axios.get(`${API_BASE_URL}/public-craftsman/`)
      .then(r => setCraftsmen(
        (r.data || [])
          .filter(i => (i.is_approved || i.status === 'approved') && (i.primary_service || i.profession))
          .map(i => ({
            ...i,
            primary_service: i.primary_service || i.profession,
            images: i.images || [],
          }))
      ))
      .catch(console.error)
      .finally(() => setLoadingCraft(false));
  },[]);

  // FIXED: trailing slash added to match Django's reviews/public/ route
  useEffect(() => {
    axios.get(`${API_BASE_URL}/reviews/public/`)
      .then(r => {
        const raw = (r.data || []).filter(x => (x.comment || x.review || x.body) && x.rating);
        const normalized = raw.slice(0, 12).map((x, idx) => ({
          comment:          x.comment || x.review || x.body || '',
          rating:           x.rating,
          reviewer:         x.reviewer || x.client_name || x.reviewed_by || x.user?.name || x.user?.username || 'Client',
          craftsman_name:   x.craftsman_name || x.craftsman?.name || x.artisan_name || '',
          craftsman_image:  x.craftsman?.profile_image || x.craftsman?.avatar || x.craftsman_image || x.craftsman_avatar || '',
          trade:            x.trade || x.service || x.craftsman?.primary_service || x.craftsman?.trade || '',
          location:         x.location || x.craftsman?.location || '',
          _i:               idx % 3,
        }));
        setReviews(normalized);
      })
      .catch(() => setReviewsError(true))
      .finally(() => setReviewsLoading(false));
  },[]);

  useEffect(() => { if(!loadingCraft&&searched) doSearch(); },[loadingCraft]); // eslint-disable-line react-hooks/exhaustive-deps

  const doSearch = (qOv) => {
    const q = (qOv !== undefined ? qOv : query).trim().toLowerCase();
    if (!q) return;
    setSearched(true);
    if (loadingCraft) {
      setResults([]);
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior:'smooth', block:'start' }), 60);
      return;
    }
    const out = craftsmen.filter(s => {
      const n   = (s.full_name || s.name || '').toLowerCase();
      const sv  = (s.primary_service  || '').toLowerCase();
      const lo  = (s.location         || '').toLowerCase();
      const de  = (s.description      || '').toLowerCase();
      const sub = (s.services || []).map(x => typeof x === 'string' ? x : (x?.name || x?.title || '')).join(' ').toLowerCase();
      return n.includes(q) || sv.includes(q) || lo.includes(q) || de.includes(q) || sub.includes(q);
    });
    setResults(out);
    setTimeout(() => resultsRef.current?.scrollIntoView({ behavior:'smooth', block:'start' }), 60);
  };

  const clearSearch = ()=>{setQuery('');setSearched(false);setResults([]);};

  const grouped = craftsmen.reduce((acc,c)=>{ const s=c.primary_service; if(!s)return acc; if(!acc[s])acc[s]=[]; acc[s].push(c); return acc; },{});

  // ── Category breakdown, derived from `grouped` (which primary_services belong to each category) ──
  const categorizedGrouped = useMemo(() => {
    const map = {};
    Object.entries(grouped).forEach(([svc, list]) => {
      const key = categorizeService(svc);
      if (!map[key]) map[key] = { services: [], count: 0 };
      map[key].services.push(svc);
      map[key].count += list.length;
    });
    return map;
  }, [grouped]);

  const categoryCovers = useMemo(() => {
    const covers = {};
    craftsmen.forEach(c => {
      const key = categorizeService(c.primary_service);
      const cover = getCover(c);
      if (!cover) return;
      const rating = Number(c.average_rating) || 0;
      if (!covers[key] || rating > covers[key].rating) {
        covers[key] = { url: cover, rating };
      }
    });
    return covers;
  }, [craftsmen]);

  const visibleGrouped = useMemo(() => {
    if (!activeCategory) return grouped;
    return Object.fromEntries(
      Object.entries(grouped).filter(([svc]) => categorizeService(svc) === activeCategory)
    );
  }, [grouped, activeCategory]);

  const statsData = useMemo(() => {
    const activeProviders = craftsmen.length;
    const jobsCompleted = reviews.length;
    const happyClients = new Set(
      reviews.map(r => (r.reviewer || '').trim().toLowerCase()).filter(Boolean)
    ).size;
    const servicesAvailable = Object.keys(grouped).length;
    return { activeProviders, jobsCompleted, happyClients, servicesAvailable };
  }, [craftsmen, reviews, grouped]);

  const openModal = (svcName, cover) => { setModalSvcName(svcName); setModalList(grouped[svcName]||[]); setModalCover(cover); setModalOpen(true); };
  const closeModal = ()=>{ setModalOpen(false); setModalSvcName(''); setModalList([]); setModalCover(''); };

  const staticReviews = [
    {comment:"The carpenter was professional, on time and the work was perfect. My furniture looks amazing!",rating:5,reviewer:"Sarah M.",craftsman_name:"James Odhiambo",craftsman_image:'',trade:"Carpentry",location:"Nairobi",_i:0},
    {comment:"My electrician fixed everything safely and charged a fair price. Would absolutely hire again.",rating:5,reviewer:"Kevin O.",craftsman_name:"Peter Mwangi",craftsman_image:'',trade:"Electrical",location:"Mombasa",_i:1},
    {comment:"The tailor made my custom outfit exactly as I described. Very pleased with the quality.",rating:4.5,reviewer:"Linda A.",craftsman_name:"Grace Wanjiku",craftsman_image:'',trade:"Tailoring",location:"Kisumu",_i:2},
  ];
  const shownReviews = (!reviewsLoading && !reviewsError && reviews.length) ? reviews : staticReviews;

  const isLoggedIn  = !!user;
  const isCraftsman = user?.role === 'craftsman';
  const isClient    = user?.role === 'client';

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Raleway:wght@400;500;600;700;800&display=swap');

        @keyframes shimmer   { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes heroSlide { 0%{transform:scale(1)} 100%{transform:scale(1.04)} }
        @keyframes fadeRight { from{opacity:0;transform:translateX(24px)} to{opacity:1;transform:translateX(0)} }
        @keyframes scrollTrack { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
        @keyframes pulseRing { 0%{box-shadow:0 0 0 0 rgba(255,240,0,.7)} 70%{box-shadow:0 0 0 14px rgba(255,240,0,0)} 100%{box-shadow:0 0 0 0 rgba(255,240,0,0)} }
        @keyframes modalIn   { from{opacity:0;transform:translateY(48px) scale(.96)} to{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes fadeIn    { from{opacity:0} to{opacity:1} }
        @keyframes floatUp   { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }

        :root {
          --green:   #000000;
          --green-d: #1a1a1a;
          --green-l: #f2f2f2;
          --gold:    #fff000;
          --gold-d:  #e0d800;
          --gold-l:  #fffde0;
          --bg:      #ffffff;
          --white:   #ffffff;
          --text:    #000000;
          --muted:   #4d4d4d;
        }
        *{box-sizing:border-box;}
        html{-webkit-text-size-adjust:100%;}
        body{background:var(--bg);font-family:'Raleway',sans-serif;color:var(--text);overflow-x:hidden;}
        img{max-width:100%;}
        .kk-skel{background:linear-gradient(90deg,#f0ede8 25%,#e8e4de 50%,#f0ede8 75%);background-size:200% 100%;animation:shimmer 1.4s infinite;}
        .kk-stars{color:var(--gold);display:inline-flex;gap:2px;font-size:.82rem;}

        /* ── HERO ── */
        .sv-hero {
          position:relative; background:var(--green-d);
          overflow:hidden; min-height:520px;
        }
        .sv-hero-bg {
          position:absolute; inset:0;
          background-size:cover; background-position:center; opacity:.22;
        }
        /* NOTE: .sv-hero-inner is also Bootstrap's .container, so we only set
           vertical padding via padding-top/bottom and give it explicit side
           padding. The old shorthand "padding:80px 0 60px" zeroed the left/right
           padding and pushed the content to the screen edge on mobile. */
        .sv-hero-inner {
          position:relative; z-index:2;
          display:flex; align-items:center;
          min-height:520px;
          padding-top:80px; padding-bottom:60px;
          padding-left:20px; padding-right:20px;
        }
        /* stop the w-100 row from shifting left via Bootstrap's negative gutter margins */
        .sv-hero-inner > .row { margin-left:0; margin-right:0; }
        .sv-hero-text { animation:floatUp .85s ease both; }
        .sv-hero-label {
          display:inline-flex; align-items:center; gap:8px;
          background:rgba(255,255,255,.12); border:1.5px solid rgba(255,255,255,.25);
          border-radius:50px; padding:6px 16px; font-size:.72rem; font-weight:700;
          color:rgba(255,255,255,.88); letter-spacing:.07em;
          text-transform:uppercase; margin-bottom:18px;
        }
        .sv-hero-label-dot {
          width:6px; height:6px; border-radius:50%;
          background:var(--gold); display:inline-block;
        }
        .sv-hero-title {
          font-family:'Raleway',sans-serif;
          font-size:clamp(1.85rem,4vw,3rem);
          font-weight:800; color:#fff; line-height:1.15; margin:0 0 16px;
        }
        .sv-hero-title em { font-style:normal; color:var(--gold); }
        .sv-hero-sub {
          font-size:.97rem; color:rgba(255,255,255,.82);
          line-height:1.85; margin:0 0 28px; max-width:480px;
        }
        .sv-hero-stats { display:flex; flex-wrap:wrap; gap:10px; margin-top:28px; }
        .sv-stat-chip {
          display:inline-flex; align-items:center; gap:7px;
          background:rgba(255,255,255,.1); border:1px solid rgba(255,255,255,.2);
          border-radius:50px; padding:6px 14px;
          font-size:.76rem; font-weight:600; color:rgba(255,255,255,.88);
        }
        .sv-stat-chip i { color:var(--gold); font-size:.72rem; }

        /* hero search pill (moved from the old standalone search section) */
        .sv-hero-search-wrap { max-width:480px; margin:0 0 20px; }
        .sv-hero-search-wrap .kk-pill-row { box-shadow:0 8px 28px rgba(0,0,0,.28); }
        .sv-hero-search-note { color:rgba(255,255,255,.68); font-size:.78rem; margin:8px 0 0; }

        /* hero CTA buttons — kept horizontal at every screen size */
        .sv-hero-actions{ display:flex; flex-wrap:nowrap; gap:12px; }
        .sv-hero-actions .kk-btn-gold,
        .sv-hero-actions .kk-btn-white-outline{ flex:1 1 0; justify-content:center; white-space:nowrap; min-width:0; }

        /* right image */
        .sv-hero-img-wrap {
          position:relative; animation:fadeRight .95s ease both; animation-delay:.15s;
          margin-top:8px;
        }
        .sv-hero-img-main {
          width:100%; height:380px; object-fit:cover; border-radius:16px;
          box-shadow:0 20px 60px rgba(0,0,0,.35); display:block;
        }
        .sv-hero-img-badge {
          position:absolute; bottom:20px; left:16px;
          background:#fff; border-radius:12px; padding:12px 16px;
          box-shadow:0 8px 28px rgba(0,0,0,.18);
          display:flex; align-items:center; gap:10px; min-width:170px;
          max-width:calc(100% - 32px);
        }
        .sv-hero-img-badge-icon {
          width:38px; height:38px; border-radius:50%;
          background:var(--gold-l); color:var(--gold-d);
          display:flex; align-items:center; justify-content:center;
          font-size:.9rem; flex-shrink:0;
        }
        .sv-hero-img-badge-val {
          font-family:'Raleway',sans-serif; font-size:1.15rem;
          font-weight:800; color:var(--text); line-height:1; margin:0;
        }
        .sv-hero-img-badge-lbl {
          font-size:.68rem; color:var(--muted); font-weight:600;
          margin:2px 0 0; display:block;
        }

        /* ── BUTTONS ── */
        .kk-btn-gold{display:inline-flex;align-items:center;gap:7px;background:var(--gold);color:#000000;border:none;border-radius:12px;padding:14px 28px;font-weight:700;font-size:.93rem;text-decoration:none;cursor:pointer;transition:all .2s;box-shadow:0 4px 18px rgba(255,240,0,.35);}
        .kk-btn-gold:hover{background:var(--gold-d);color:#000000;transform:translateY(-2px);box-shadow:0 8px 28px rgba(255,240,0,.45);}
        .kk-btn-green-outline{display:inline-flex;align-items:center;gap:7px;background:var(--gold);color:#000000;border:2px solid var(--gold);border-radius:12px;padding:12px 26px;font-weight:700;font-size:.93rem;text-decoration:none;cursor:pointer;transition:all .2s;}
        .kk-btn-green-outline:hover{background:var(--gold-d);color:#000000;border-color:var(--gold-d);transform:translateY(-2px);}
        .kk-btn-white-outline{display:inline-flex;align-items:center;gap:7px;background:rgba(255,255,255,.15);color:#fff;border:2px solid rgba(255,255,255,.6);border-radius:12px;padding:12px 26px;font-weight:700;font-size:.93rem;text-decoration:none;cursor:pointer;transition:all .2s;backdrop-filter:blur(4px);}
        .kk-btn-white-outline:hover{background:rgba(255,255,255,.28);color:#fff;transform:translateY(-2px);}
        .kk-btn-hire-sm{display:block;text-align:center;background:var(--green);color:#fff;border:none;border-radius:10px;padding:11px 0;font-weight:700;font-size:.86rem;text-decoration:none;cursor:pointer;transition:all .18s;margin-top:auto;}
        .kk-btn-hire-sm:hover{background:var(--green-d);color:#fff;transform:translateY(-1px);}
        .kk-btn-muted{background:#e4e4e4!important;color:#aaa!important;cursor:default!important;transform:none!important;}

        /* ── SEARCH PILL (shared styles — now used inside the hero) ── */
        .kk-pill-wrap{width:100%;max-width:660px;margin:0 auto;}
        .kk-pill-row{display:flex;align-items:center;background:#fff;border:2px solid #e7e5e4;border-radius:50px;box-shadow:0 3px 16px rgba(0,0,0,.1);overflow:hidden;transition:border-color .18s,box-shadow .18s;}
        .kk-pill-row:focus-within{border-color:var(--gold);box-shadow:0 0 0 4px rgba(255,240,0,.18),0 3px 16px rgba(0,0,0,.08);}
        .kk-pill-ico{flex-shrink:0;padding:0 4px 0 18px;color:#a8a29e;font-size:.92rem;pointer-events:none;}
        .kk-pill-input{flex:1;border:none;outline:none;background:transparent;font-family:'Raleway',sans-serif;font-size:.95rem;font-weight:500;color:var(--text);padding:14px 10px;min-width:0;}
        .kk-pill-input::placeholder{color:#a8a29e;}
        .kk-pill-btn{flex-shrink:0;background:var(--gold);color:#000000;border:none;border-radius:0 48px 48px 0;padding:0 22px;height:100%;min-height:50px;font-family:'Raleway',sans-serif;font-weight:700;font-size:.88rem;cursor:pointer;display:flex;align-items:center;gap:6px;white-space:nowrap;transition:background .18s;}
        .kk-pill-btn:hover:not(:disabled){background:var(--gold-d);}
        .kk-pill-btn:disabled{background:var(--gold);opacity:1;cursor:not-allowed;}
        @media(max-width:400px){
          .kk-pill-btn{padding:0 14px;font-size:.82rem;}
          .kk-pill-btn .kk-btn-label{display:none;}
          .kk-pill-input{font-size:.88rem;padding:12px 8px;}
          .kk-pill-ico{padding:0 2px 0 14px;}
        }
        .kk-search-pill{display:inline-flex;align-items:center;gap:8px;background:rgba(255,255,255,.12);color:rgba(255,255,255,.85);border:1.5px solid rgba(255,255,255,.3);border-radius:30px;padding:8px 20px;font-size:.82rem;font-weight:600;text-decoration:none;transition:all .2s;margin-top:16px;}
        .kk-search-pill:hover{background:rgba(255,255,255,.22);color:#fff;transform:translateY(-2px);}

        /* ── RESULTS ── */
        .kk-results-section{background:#f5f5f0;padding:40px 0 60px;}
        .kk-results-bar{display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:10px;padding:22px 0 20px;border-bottom:1.5px solid #e4e4e4;margin-bottom:28px;}
        .kk-results-tag{background:var(--green-l);color:var(--green);border:1px solid #a5d6a7;border-radius:20px;padding:5px 18px;font-size:.84rem;font-weight:600;}
        .kk-clear-btn{background:#fff;border:1.5px solid #e0e0e0;color:var(--muted);border-radius:20px;padding:5px 16px;font-size:.82rem;font-weight:600;cursor:pointer;display:flex;align-items:center;gap:6px;transition:all .15s;}
        .kk-clear-btn:hover{background:#fee2e2;color:#dc2626;border-color:#fca5a5;}

        /* ── CARDS ── */
        .kk-card{background:#fff;border-radius:14px;overflow:hidden;box-shadow:0 2px 16px rgba(0,0,0,.07);transition:transform .25s,box-shadow .25s,border-color .2s;display:flex;flex-direction:column;height:100%;border:1.5px solid #ede9e2;position:relative;}
        .kk-card::before{content:'';position:absolute;top:0;left:0;right:0;height:3px;background:linear-gradient(to right,var(--green),var(--gold));transform:scaleX(0);transform-origin:left;transition:transform .3s;z-index:1;}
        .kk-card:hover{transform:translateY(-5px);box-shadow:0 12px 36px rgba(0,0,0,.12);border-color:#c8e6c9;}
        .kk-card:hover::before{transform:scaleX(1);}
        .kk-card-cover{position:relative;height:200px;overflow:hidden;flex-shrink:0;}
        .kk-card-img{width:100%;height:100%;object-fit:cover;transition:transform .4s;}
        .kk-card:hover .kk-card-img{transform:scale(1.06);}
        .kk-badge-trade{position:absolute;top:10px;left:10px;background:rgba(25,135,84,.88);color:#fff;border-radius:20px;padding:3px 11px;font-size:.7rem;font-weight:700;backdrop-filter:blur(4px);}
        .kk-badge-top{position:absolute;top:10px;right:10px;background:rgba(255,240,0,.93);color:#000000;border-radius:20px;padding:3px 10px;font-size:.67rem;font-weight:800;}
        .kk-card-body{padding:16px;display:flex;flex-direction:column;gap:9px;flex:1;}
        .kk-card-profile{display:flex;align-items:center;gap:10px;}
        .kk-avatar{width:44px;height:44px;border-radius:50%;object-fit:cover;border:2.5px solid var(--gold);flex-shrink:0;}
        .kk-card-name{font-size:.93rem;font-weight:700;color:var(--text);margin:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .kk-card-loc{font-size:.74rem;color:var(--green);display:block;margin-top:1px;}
        .kk-card-desc{font-size:.78rem;color:var(--muted);line-height:1.55;margin:0;flex:1;}
        .kk-sub-tag{background:var(--green-l);color:var(--green);border-radius:20px;padding:2px 9px;font-size:.69rem;font-weight:600;}
        .kk-phone-pill{display:inline-flex;align-items:center;background:#f1f3f5;color:#495057;border-radius:20px;padding:4px 12px;font-size:.74rem;font-weight:600;text-decoration:none;transition:all .15s;width:fit-content;margin-top:2px;}
        .kk-phone-pill:hover{background:var(--green);color:#fff;}

        /* ── HOW IT WORKS (radial, gray background) ── */
        .kk-hiw-radial{
          background:#f5f5f0;
          padding:100px 0 110px;
          position:relative;
          overflow:hidden;
        }

        .kk-hiw-radial-title{
          text-align:center;
          color:#222222;
          font-family:'Raleway',sans-serif;
          font-size:clamp(2.1rem,5vw,3.4rem);
          font-weight:800;
          letter-spacing:-.01em;
          margin:0 0 64px;
        }

        .kk-hiw-radial-title em{
          font-style:italic;
          font-weight:400;
          color:#D4AF37;
          font-family:Georgia,'Times New Roman',serif;
        }
        .kk-hiw-orbit{
          position:relative;width:100%;max-width:640px;aspect-ratio:1/1;
          margin:0 auto;
        }
        .kk-hiw-ring{
          position:absolute;
          inset:9%;
          border-radius:50%;
          border:1.5px dashed rgba(0,0,0,.85);
          pointer-events:none;
        }
        .kk-hiw-center{
          position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);
          width:56%;aspect-ratio:1/1;border-radius:50%;background:var(--gold);
          display:flex;flex-direction:column;align-items:center;justify-content:center;
          text-align:center;padding:10%;box-shadow:0 20px 60px rgba(255,240,0,.28);
          z-index:2;
        }
        .kk-hiw-center-eyebrow{
          font-size:.72rem;font-weight:800;letter-spacing:.14em;text-transform:uppercase;
          color:#000;opacity:.65;
        }
        .kk-hiw-center-main{
          font-family:'Raleway',sans-serif;font-weight:800;color:#000;
          font-size:clamp(1.15rem,2.6vw,1.55rem);line-height:1.22;margin:8px 0;
        }

        .kk-hiw-node{
          position:absolute;width:clamp(150px,20vw,205px);
          background:#fff;border-radius:14px;padding:16px 18px;
          box-shadow:0 10px 30px rgba(0,0,0,.35);z-index:3;
          transition:transform .25s,box-shadow .25s;
        }
        .kk-hiw-node:hover{transform:translateY(-4px);box-shadow:0 16px 42px rgba(255,240,0,.25);}
        .kk-hiw-node-num{font-size:.7rem;font-weight:800;color:#a8a29e;letter-spacing:.05em;display:block;margin-bottom:4px;}
        .kk-hiw-node-title{font-weight:800;color:#000;font-size:.95rem;margin:0 0 6px;}
        .kk-hiw-node-body{font-size:.78rem;color:var(--muted);line-height:1.55;margin:0;}

        .kk-hiw-node-tl{top:4%;left:0;}
        .kk-hiw-node-tr{top:4%;right:0;}
        .kk-hiw-node-bl{bottom:4%;left:0;}
        .kk-hiw-node-br{bottom:4%;right:0;}

        @media(max-width:767px){
          .kk-hiw-radial{padding:56px 0 48px;}
          .kk-hiw-radial-title{margin-bottom:28px;font-size:clamp(1.55rem,7vw,2.1rem);}
          .kk-hiw-orbit{max-width:400px;}
          .kk-hiw-center{width:42%;padding:5%;}
          .kk-hiw-center-eyebrow{font-size:.56rem;}
          .kk-hiw-center-main{font-size:clamp(.8rem,3.4vw,1rem);margin:5px 0;}
          .kk-hiw-node{
            width:clamp(96px,29vw,150px);
            padding:10px 11px;
            border-radius:10px;
          }
          .kk-hiw-node-num{font-size:.6rem;margin-bottom:2px;}
          .kk-hiw-node-title{font-size:.76rem;margin:0 0 3px;}
          .kk-hiw-node-body{font-size:.63rem;line-height:1.4;}
          .kk-hiw-node-tl{top:0;left:0;}
          .kk-hiw-node-tr{top:0;right:0;}
          .kk-hiw-node-bl{bottom:0;left:0;}
          .kk-hiw-node-br{bottom:0;right:0;}
        }

        @media(max-width:420px){
          .kk-hiw-orbit{max-width:320px;}
          .kk-hiw-center{width:40%;}
          .kk-hiw-node{width:clamp(84px,30vw,120px);padding:8px 9px;}
          .kk-hiw-node-title{font-size:.7rem;}
          .kk-hiw-node-body{font-size:.58rem;line-height:1.3;}
        }

        /* ── STATS (flat, live numbers, count-up animation, no icons) ── */
        .kk-stats{
          background:#fff;
          padding:72px 0;
        }

        .kk-stats-title{
          font-family:'Raleway',sans-serif;
          font-size:clamp(1.4rem,3vw,2.2rem);
          font-weight:800;
          color:var(--text);
        }

        .kk-stats-sub{
          color:var(--muted);
          font-size:.95rem;
        }

        .kk-stats-grid{
          display:grid;
          grid-template-columns:repeat(4,1fr);
          gap:0;
          max-width:920px;
          margin:8px auto 0;
        }


        .kk-stat-flat{
          text-align:center;
          padding:10px 20px;
          border-left:1px solid #e6e6e6;
        }
        .kk-stat-flat:first-child{border-left:none;}
        .kk-stat-flat-value{
          font-family:'Raleway',sans-serif;
          font-size:clamp(1.9rem,4.2vw,3rem);
          font-weight:800;
          color:#FFC107;
          margin:0;
          line-height:1;
          font-variant-numeric:tabular-nums;
        }
        .kk-stat-flat-suffix{color:var(--gold-d);}
        .kk-stat-flat-label{
          font-size:.76rem;
          font-weight:700;
          color:var(--muted);
          margin:9px 0 0;
          text-transform:uppercase;
          letter-spacing:.06em;
        }
        @media(max-width:576px){
          .kk-stats-grid{grid-template-columns:repeat(2,1fr);row-gap:28px;}
          .kk-stat-flat:nth-child(2n+1){border-left:none;}
          .kk-stat-flat:nth-child(-n+2){padding-top:0;}
        }

        /* ── SERVICES ── */
        .kk-services{background:#f5f5f0;padding:72px 0;}

        /* ── SERVICE CATEGORIES (top-level filter above CoverFlow) ── */
        .kk-cat-grid{
          display:grid;
          grid-template-columns:repeat(5,1fr);
          gap:16px;
        }
        .kk-cat-card{
          background:#fff;border:1.5px solid #ede9e2;border-radius:14px;
          overflow:hidden;cursor:pointer;
          display:flex;flex-direction:column;
          transition:transform .2s,box-shadow .2s,border-color .2s;
          font-family:'Raleway',sans-serif;padding:0;text-align:left;
        }
        .kk-cat-card:hover{transform:translateY(-4px);box-shadow:0 10px 28px rgba(0,0,0,.1);border-color:var(--gold);}
        .kk-cat-card.active{border-color:var(--gold);box-shadow:0 0 0 2px var(--gold);}
        .kk-cat-img-wrap{position:relative;width:100%;height:110px;overflow:hidden;flex-shrink:0;}
        .kk-cat-img{width:100%;height:100%;object-fit:cover;transition:transform .4s;display:block;}
        .kk-cat-card:hover .kk-cat-img{transform:scale(1.08);}
        .kk-cat-img-overlay{position:absolute;inset:0;background:linear-gradient(to top,rgba(0,0,0,.45) 0%,rgba(0,0,0,0) 55%);transition:background .2s;}
        .kk-cat-card.active .kk-cat-img-overlay{background:linear-gradient(to top,rgba(0,0,0,.55) 0%,rgba(255,240,0,.12) 100%);}
        .kk-cat-label{font-weight:700;font-size:.85rem;color:var(--text);padding:12px 14px 0;}
        .kk-cat-count{font-size:.72rem;color:var(--muted);padding:0 14px;display:block;margin-top:2px;}
        .kk-cat-explore{font-size:.72rem;font-weight:700;color:var(--green);padding:6px 14px 14px;display:block;}
        @media(max-width:992px){.kk-cat-grid{grid-template-columns:repeat(3,1fr);}}
        @media(max-width:576px){.kk-cat-grid{grid-template-columns:repeat(2,1fr);} .kk-cat-img-wrap{height:90px;}}

        .kk-svc-card{width:100%;cursor:pointer;border-radius:14px;outline:none;background:none;border:none;padding:0;display:block;}
        .kk-svc-inner{border-radius:14px;overflow:hidden;box-shadow:0 2px 16px rgba(0,0,0,.08);transition:transform .25s,box-shadow .25s;background:#fff;border:1.5px solid #ede9e2;}
        .kk-svc-card:hover .kk-svc-inner{transform:translateY(-6px);box-shadow:0 14px 36px rgba(0,0,0,.14);}
        .kk-svc-img-wrap{position:relative;height:220px;overflow:hidden;}
        .kk-svc-img{width:100%;height:100%;object-fit:cover;transition:transform .4s;}
        .kk-svc-card:hover .kk-svc-img{transform:scale(1.06);}
        .kk-svc-overlay{position:absolute;inset:0;background:rgba(25,135,84,0);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;transition:background .25s;pointer-events:none;}
        .kk-svc-card:hover .kk-svc-overlay{background:rgba(25,135,84,.62);}
        .kk-svc-overlay-label{opacity:0;transform:translateY(10px);transition:opacity .2s,transform .2s;background:rgba(0,0,0,.3);color:#fff;border-radius:20px;padding:7px 18px;font-size:.85rem;font-weight:700;display:flex;align-items:center;gap:6px;}
        .kk-svc-overlay-count{opacity:0;transform:translateY(10px);transition:opacity .2s .06s,transform .2s .06s;background:var(--gold);color:#000000;border-radius:20px;padding:3px 14px;font-size:.75rem;font-weight:800;}
        .kk-svc-card:hover .kk-svc-overlay-label,.kk-svc-card:hover .kk-svc-overlay-count{opacity:1;transform:translateY(0);}
        .kk-svc-body{padding:16px;text-align:center;}
        .kk-svc-name{font-family:'Raleway',sans-serif;font-size:1.05rem;font-weight:700;color:var(--text);margin:0 0 4px;}
        .kk-svc-loc{font-size:.8rem;color:var(--green);font-weight:600;display:block;margin-bottom:10px;}
        .kk-svc-cta{background:var(--green);color:#fff;border-radius:8px;padding:9px 0;font-size:.82rem;font-weight:700;text-align:center;transition:background .18s;}
        .kk-svc-card:hover .kk-svc-cta{background:var(--green-d);}
        .kk-svc-track-wrap{overflow:hidden;position:relative;margin:0 -12px;padding:20px 0 24px;-webkit-mask:linear-gradient(to right,transparent 0%,#f5f5f0 7%,#f5f5f0 93%,transparent 100%);mask:linear-gradient(to right,transparent 0%,#f5f5f0 7%,#f5f5f0 93%,transparent 100%);}
        .kk-svc-track-wrap:hover .kk-svc-track{animation-play-state:paused;}
        .kk-svc-track{display:flex;gap:22px;width:max-content;animation:scrollTrack 40s linear infinite;padding:0 12px;}
        .kk-svc-track-item{width:280px;flex-shrink:0;}

        /* ── MODAL ── */
        .kk-modal-backdrop{position:fixed;inset:0;z-index:10000;background:rgba(0,0,0,.65);display:flex;align-items:flex-end;justify-content:center;animation:fadeIn .2s ease;padding:0;}
        .kk-modal-panel{background:#f4f6f8;border-radius:22px 22px 0 0;width:100%;max-width:1140px;max-height:92vh;display:flex;flex-direction:column;animation:modalIn .3s cubic-bezier(.34,1.4,.64,1);overflow:hidden;box-shadow:0 -8px 40px rgba(0,0,0,.25);}
        .kk-modal-hero{position:relative;height:175px;flex-shrink:0;overflow:hidden;}
        .kk-modal-hero-img{width:100%;height:100%;object-fit:cover;display:block;}
        .kk-modal-hero-overlay{position:absolute;inset:0;background:linear-gradient(to bottom,rgba(0,0,0,.05) 0%,rgba(0,0,0,.72) 100%);}
        .kk-modal-hero-content{position:absolute;bottom:0;left:0;right:0;padding:18px 22px;display:flex;flex-direction:column;gap:6px;}
        .kk-modal-badge{display:inline-flex;align-items:center;background:var(--gold);color:#000000;border-radius:20px;padding:3px 13px;font-size:.72rem;font-weight:800;width:fit-content;}
        .kk-modal-title{font-family:'Raleway',sans-serif;font-size:clamp(1.2rem,3vw,1.9rem);font-weight:800;color:#fff;margin:0;}
        .kk-modal-sub{font-size:.82rem;color:rgba(255,255,255,.82);margin:0;}
        .kk-modal-close{position:absolute;top:14px;right:14px;width:36px;height:36px;border-radius:50%;background:rgba(255,255,255,.18);backdrop-filter:blur(6px);border:1.5px solid rgba(255,255,255,.35);color:#fff;display:flex;align-items:center;justify-content:center;font-size:.88rem;cursor:pointer;transition:all .15s;z-index:2;}
        .kk-modal-close:hover{background:rgba(220,38,38,.85);border-color:transparent;}
        .kk-modal-body{overflow-y:auto;padding:24px;flex:1;min-height:0;}
        .kk-modal-card{background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 2px 12px rgba(0,0,0,.07);transition:transform .25s,box-shadow .25s;display:flex;flex-direction:column;height:100%;}
        .kk-modal-card:hover{transform:translateY(-4px);box-shadow:0 10px 28px rgba(0,0,0,.12);}
        .kk-modal-card-cover{position:relative;height:150px;overflow:hidden;flex-shrink:0;}
        .kk-modal-card-img{width:100%;height:100%;object-fit:cover;transition:transform .4s;}
        .kk-modal-card:hover .kk-modal-card-img{transform:scale(1.06);}
        .kk-modal-card-body{padding:14px;display:flex;flex-direction:column;gap:8px;flex:1;}
        .kk-modal-btn-outline{flex:1;min-width:0;text-align:center;background:var(--green-l);color:var(--green);border:1.5px solid var(--green);border-radius:9px;padding:9px 6px;font-weight:700;font-size:.8rem;text-decoration:none;transition:all .18s;cursor:pointer;font-family:'Raleway',sans-serif;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .kk-modal-btn-outline:hover{background:var(--green);color:#fff;}
        .kk-modal-btn-hire{flex:1;min-width:0;text-align:center;background:var(--green);color:#fff;border:none;border-radius:9px;padding:9px 6px;font-weight:700;font-size:.8rem;text-decoration:none;transition:background .18s;cursor:pointer;font-family:'Raleway',sans-serif;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .kk-modal-btn-hire:hover{background:var(--green-d);color:#fff;}

        /* ── ABOUT / HIRE / TESTI ── */
        .kk-about{background:var(--white);padding:72px 0;}
        .kk-carousel-wrap{height:400px;border-radius:16px;overflow:hidden;box-shadow:0 8px 32px rgba(0,0,0,.1);}
        .kk-carousel-img{width:100%;height:100%;object-fit:cover;display:block;}
        .kk-hire{background:#f5f5f0;padding:72px 0;}
        .kk-hire-img-wrap{position:relative;min-height:300px;}
        .kk-hire-main{border-radius:14px;box-shadow:0 4px 24px rgba(0,0,0,.1);width:100%;max-height:360px;object-fit:cover;display:block;}
        .kk-hire-f1{position:absolute;top:-18px;right:-8px;width:44%;border-radius:12px;box-shadow:0 4px 18px rgba(0,0,0,.1);}
        .kk-hire-f2{position:absolute;bottom:-18px;left:-8px;width:44%;border-radius:12px;box-shadow:0 4px 18px rgba(0,0,0,.1);}
        .kk-testi{background:var(--white);padding:72px 0;overflow:hidden;}
        .kk-testi-track-wrap{overflow:hidden;position:relative;-webkit-mask:linear-gradient(to right,transparent 0%,#fff 8%,#fff 92%,transparent 100%);mask:linear-gradient(to right,transparent 0%,#fff 8%,#fff 92%,transparent 100%);}
        .kk-testi-track-wrap:hover .kk-testi-track{animation-play-state:paused;}
        .kk-testi-track{display:flex;gap:22px;width:max-content;animation:scrollTrack 32s linear infinite;}
        .kk-testi-card{background:#fff;border-radius:14px;padding:22px;box-shadow:0 2px 16px rgba(0,0,0,.07);width:clamp(240px,80vw,300px);flex-shrink:0;border:1.5px solid #ede9e2;transition:transform .3s,box-shadow .3s;}
        .kk-testi-card:hover{transform:translateY(-5px);box-shadow:0 12px 36px rgba(255,240,0,.2);}
        .kk-testi-top{display:flex;align-items:center;gap:12px;margin-bottom:12px;}
        .kk-testi-avatar{width:48px;height:48px;border-radius:50%;object-fit:cover;border:3px solid var(--gold);flex-shrink:0;}
        .kk-testi-name{font-weight:700;color:var(--text);margin:0 0 1px;font-size:.88rem;}
        .kk-testi-loc{font-size:.72rem;color:var(--muted);margin:0 0 3px;}
        .kk-testi-comment{font-size:.84rem;color:#495057;font-style:italic;line-height:1.65;margin:0;}

        /* ── FAQ ── */
        .kk-faq{background:#f5f5f0;padding:72px 0;}
        .kk-faq-item{border:none!important;border-radius:12px!important;margin-bottom:10px;box-shadow:0 2px 12px rgba(0,0,0,.06);overflow:hidden;transition:transform .2s,box-shadow .2s;}
        .kk-faq-item:hover{transform:translateY(-2px);box-shadow:0 6px 22px rgba(0,0,0,.09);}
        .kk-faq-btn{background:#fff!important;font-weight:600;font-size:.93rem;padding:17px 22px;border:none;font-family:'Raleway',sans-serif;}
        .accordion-button:not(.collapsed){color:var(--green)!important;box-shadow:none!important;}
        .accordion-button::after{background-image:none!important;content:"+";font-size:1.35rem;font-weight:700;color:var(--green);}
        .accordion-button:not(.collapsed)::after{content:"−";}
        .kk-faq-body{background:#fff;color:var(--muted);padding:4px 22px 20px;font-size:.88rem;line-height:1.75;}
        .kk-q-card{background:#fff;border-radius:20px;padding:32px 26px;max-width:420px;margin:0 auto;box-shadow:0 2px 20px rgba(0,0,0,.08);}
        .kk-q-blob{width:110px;height:96px;background:var(--gold);border-radius:40% 60% 50% 50%;display:flex;align-items:center;justify-content:center;margin:0 auto 18px;transition:transform .3s;cursor:default;}
        .kk-q-blob:hover{transform:scale(1.06) rotate(-3deg);}
        .kk-q-mark{font-size:3.4rem;font-weight:900;color:#fff;line-height:1;}
        .kk-q-area{border-radius:10px;padding:12px 14px;border:1.5px solid #e9ecef;width:100%;resize:vertical;font-size:.9rem;transition:border-color .2s,box-shadow .2s;font-family:'Raleway',sans-serif;}
        .kk-q-area:focus{border-color:var(--green);box-shadow:0 0 0 3px rgba(25,135,84,.1);outline:none;}
        .kk-q-send{background:var(--green);color:#fff;border:none;border-radius:10px;padding:12px;font-weight:700;font-size:.92rem;width:100%;margin-top:12px;cursor:pointer;transition:background .18s;font-family:'Raleway',sans-serif;}
        .kk-q-send:hover:not(:disabled){background:var(--green-d);}
        .kk-q-send:disabled{background:var(--green);opacity:1;cursor:not-allowed;}
        .kk-q-success{background:var(--green-l);color:var(--green);border-radius:10px;padding:14px;text-align:center;font-weight:600;font-size:.88rem;}

        /* ── SECTION HELPERS ── */
        .kk-section-title{font-family:'Raleway',sans-serif;font-size:clamp(1.5rem,3.5vw,2.4rem);font-weight:800;color:var(--text);margin:0 0 10px;}
        .kk-section-sub{font-size:.95rem;color:var(--muted);line-height:1.75;margin:0;}

        /* ── FOOTER ── */
        .kk-footer{background:#000000;color:#bbb;padding:52px 0 26px;font-family:'Raleway',sans-serif;}
        .kk-footer h5{color:#fff;font-weight:700;font-size:.8rem;letter-spacing:.08em;text-transform:uppercase;margin-bottom:14px;}
        .kk-footer a{color:#bbb;text-decoration:none;font-size:.85rem;transition:color .15s;}
        .kk-footer a:hover{color:var(--gold);}
        .kk-footer p{font-size:.85rem;margin-bottom:6px;}
        .kk-footer-hr{border-color:rgba(255,255,255,.08);margin:26px 0 18px;}
        .kk-footer-bottom{display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:10px;font-size:.8rem;}
        .kk-social a{font-size:1rem;color:#bbb;margin-right:14px;display:inline-block;transition:color .15s,transform .15s;}
        .kk-social a:hover{color:var(--gold);transform:translateY(-2px);}
        .kk-map-wrap{border-radius:12px;overflow:hidden;box-shadow:0 4px 20px rgba(0,0,0,.3);}
        .kk-back-top{position:fixed;bottom:24px;right:24px;width:44px;height:44px;border-radius:50%;background:var(--green);color:#fff;border:none;font-size:.9rem;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 18px rgba(25,135,84,.4);cursor:pointer;transition:all .2s;z-index:9999;opacity:0;pointer-events:none;}
        .kk-back-top.on{opacity:1;pointer-events:auto;}
        .kk-back-top:hover{background:var(--green-d);transform:translateY(-2px);}
        .kk-empty{text-align:center;padding:56px 20px;}
        .kk-empty-icon{font-size:3rem;margin-bottom:14px;display:block;}
        .kk-empty h5{font-weight:700;color:var(--muted);}
        .kk-empty p{color:var(--muted);font-size:.9rem;}

        /* ── RESPONSIVE ── */

        @media(max-width:992px){
          .sv-hero-inner{min-height:auto;padding-top:64px;padding-bottom:40px;}
          .sv-hero{min-height:auto;}
          .sv-hero-inner > .row > [class*="col-"]{padding-left:0;padding-right:0;}
          .sv-hero-inner > .row{--bs-gutter-y:0;}
          .sv-hero-img-wrap{margin-top:24px;}
          .kk-stats-title{font-size:1.7rem;}
          .kk-hire-img-wrap{margin-top:24px;}
        }

        @media(max-width:768px){
          .sv-hero-inner{padding-top:56px;padding-bottom:36px;}
          .sv-hero-title{font-size:1.9rem;}
          .sv-hero-sub{font-size:.9rem;max-width:100%;}
          .sv-hero-img-main{height:240px;}
          .sv-hero-img-wrap{margin-top:24px;}
          .sv-hero-img-badge{left:12px;bottom:12px;padding:10px 14px;min-width:150px;}
          .sv-hero-search-wrap{max-width:100%;}

          .kk-hiw-radial,.kk-stats,.kk-services,.kk-hire,.kk-about,.kk-testi,.kk-faq{padding:52px 0;}

          .kk-hire-f1,.kk-hire-f2{display:none;}
          .kk-hire-img-wrap{min-height:0;}
          .kk-hire-main{max-height:260px;}

          .kk-carousel-wrap{height:240px;}

          .kk-modal-backdrop{padding:0;}
          .kk-modal-panel{border-radius:20px 20px 0 0;max-height:94vh;}

          .kk-svc-img-wrap{height:170px;}

          .kk-results-bar{flex-direction:column;align-items:flex-start;}
        }

        @media(max-width:576px){
          .sv-hero-inner{padding-left:16px;padding-right:16px;}
          .sv-hero-title{font-size:1.65rem;}
          .sv-hero-sub{font-size:.86rem;}
          .sv-hero-img-main{height:200px;}
          .kk-section-title{font-size:1.5rem;}
          .kk-footer-bottom{flex-direction:column;text-align:center;}
          .kk-q-card{padding:24px 18px;}
          .kk-modal-title{font-size:1.3rem;}
          .kk-hire-main{max-height:220px;}

          .sv-hero-actions{ gap:8px; }
          .sv-hero-actions .kk-btn-gold,
          .sv-hero-actions .kk-btn-white-outline{
            padding:12px 10px;
            font-size:.8rem;
            gap:5px;
          }
        }

        @media(max-width:360px){
          .sv-hero-actions .kk-btn-gold,
          .sv-hero-actions .kk-btn-white-outline{
            font-size:.72rem;
            padding:11px 6px;
            gap:4px;
          }
        }

        @media(max-width:420px){
          .kk-modal-hero{height:150px;}
          .kk-avatar{width:38px;height:38px;}
          .kk-testi-avatar{width:40px;height:40px;}
          .kk-testi-card{padding:16px;}
          .kk-card-body{padding:14px;}
          .kk-q-blob{width:88px;height:78px;}
          .kk-q-mark{font-size:2.6rem;}
          .sv-hero-img-badge{min-width:0;}
          .sv-hero-img-badge-val{font-size:1rem;}
        }

        @media(min-width:768px){
          .kk-modal-backdrop{align-items:center;padding:20px;}
          .kk-modal-panel{border-radius:20px;max-height:90vh;}
          .kk-modal-hero{height:210px;}
        }

        @media(min-width:1400px){
          .container{max-width:1240px;}
        }
      `}</style>

      <ServiceModal isOpen={modalOpen} onClose={closeModal} serviceName={modalSvcName} craftsmen={modalList} coverImg={modalCover} navigate={navigate} user={user}/>

      {/* ━━━━━━━━━━ HERO (search bar lives here) ━━━━━━━━━━ */}
      <section className="sv-hero" id="top">
        <div className="sv-hero-bg" style={{ backgroundImage:`url(${heroImage})` }} aria-hidden="true"/>
        <div className="container sv-hero-inner">
          <div className="row align-items-center w-100 g-5">

            {/* LEFT — text + search */}
            <div className="col-lg-6 sv-hero-text">

              <h1 className="sv-hero-title">
                Find a Trusted Service Provider <br/>For Any Job Near You<br/>
              </h1>

              <p className="sv-hero-sub">
                Connect with trusted plumbers, electricians, mechanics,
                carpenters and other skilled professionals near you.
              </p>

              <div className="sv-hero-search-wrap">
                <div className="kk-pill-row">
                  <span className="kk-pill-ico" aria-hidden="true"><i className="fas fa-search"/></span>
                  <input
                    type="search"
                    className="kk-pill-input"
                    placeholder="Search by service, name or location…"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && doSearch(query)}
                    aria-label="Search service providers"
                  />
                  <button
                    className="kk-pill-btn"
                    onClick={() => doSearch(query)}
                    disabled={!query.trim()}
                    aria-label="Search"
                  >
                    <i className="fas fa-search"/>
                    <span className="kk-btn-label">Search</span>
                  </button>
                </div>
                {loadingCraft && (
                  <p className="sv-hero-search-note">
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"/>
                    Loading providers…
                  </p>
                )}
              </div>

            </div>

            {/* RIGHT — image */}
            <div className="col-lg-6 sv-hero-img-wrap">
              <img
                src={pipersImage}
                alt="Plumbers"
                className="sv-hero-img-main"
              />
              <div className="sv-hero-img-badge">
                <div className="sv-hero-img-badge-icon">
                  <i className="fas fa-star"/>
                </div>
                <div>
                  <p className="sv-hero-img-badge-val">4.8 / 5</p>
                  <span className="sv-hero-img-badge-lbl">Average craftsman rating</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━ HOW IT WORKS (radial) ━━━━━━━━━━ */}
      <section className="kk-hiw-radial" id="how-it-works">
        <div className="container">
          <h2 className="kk-hiw-radial-title" data-aos="fade-up">
            How it <em>works.</em>
          </h2>
          <div className="kk-hiw-orbit">
            <div className="kk-hiw-ring" aria-hidden="true"/>
            <div className="kk-hiw-center">
              <span className="kk-hiw-center-eyebrow">From idea</span>
              <span className="kk-hiw-center-main">to your doorstep</span>
              <span className="kk-hiw-center-eyebrow">in 4 steps</span>
            </div>
            {[
              {n:'01',pos:'tl',title:'Search',body:'Explore verified service providers and their portfolios.'},
              {n:'02',pos:'tr',title:'Connect',body:'Message the provider, agree on the job and price.'},
              {n:'03',pos:'bl',title:'The Work',body:'Follow along as your job gets done, start to finish.'},
              {n:'04',pos:'br',title:'Delivered',body:'Job complete — rate and review your provider.'},
            ].map(({n,pos,title,body})=>(
              <div className={`kk-hiw-node kk-hiw-node-${pos}`} key={n} data-aos="fade-up" data-aos-delay={Number(n)*80}>
                <span className="kk-hiw-node-num">{n}</span>
                <h3 className="kk-hiw-node-title">{title}</h3>
                <p className="kk-hiw-node-body">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━ SERVICES ━━━━━━━━━━ */}
      <section className="kk-services" id="services">
        <div className="container">
          <div className="text-center mb-5" data-aos="fade-up">
            <h2 className="kk-section-title">Explore Our Services</h2>
            <p className="kk-section-sub">Pick a category to narrow things down, then click a service card to see all verified providers in it.</p>
          </div>

          {!loadingCraft && (
            <div className="kk-cat-grid mb-5" data-aos="fade-up">
              {SERVICE_CATEGORIES.map(cat => {
                const info = categorizedGrouped[cat.key];
                if (!info || info.services.length === 0) return null; // hide categories with no matching services
                const isActive = activeCategory === cat.key;
                return (
                  <button
                    key={cat.key}
                    type="button"
                    className={`kk-cat-card ${isActive ? 'active' : ''}`}
                    onClick={() => setActiveCategory(isActive ? null : cat.key)}
                  >
                    <span className="kk-cat-img-wrap">
                      <img src={categoryCovers[cat.key]?.url || PLACEHOLDER} alt={cat.label} className="kk-cat-img" loading="lazy"
                        onError={e=>{ e.target.src=PLACEHOLDER; }}/>
                      <span className="kk-cat-img-overlay" aria-hidden="true"/>
                    </span>
                    <span className="kk-cat-label">{cat.label}</span>
                    <span className="kk-cat-count">{info.count} provider{info.count!==1?'s':''}</span>
                    <span className="kk-cat-explore">{isActive ? 'Showing ✕' : 'Explore →'}</span>
                  </button>
                );
              })}
            </div>
          )}

          {activeCategory && (
            <div className="kk-results-bar" style={{marginTop:-10}}>
              <span className="kk-results-tag">
                Showing <strong>{SERVICE_CATEGORIES.find(c=>c.key===activeCategory)?.label}</strong>
              </span>
              <button className="kk-clear-btn" onClick={() => setActiveCategory(null)}>
                <i className="fas fa-times"/>Show all services
              </button>
            </div>
          )}

          {loadingCraft ? (
            <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-4 mt-3">
              {[...Array(6)].map((_,i) => <SkeletonCard key={i}/>)}
            </div>
          ) : Object.keys(visibleGrouped).length === 0 ? (
            <div className="kk-empty mt-4">
              <span className="kk-empty-icon">🔍</span>
              <h5>{activeCategory ? 'No service providers in this category yet' : 'No service providers available yet'}</h5>
              <p>Check back soon.</p>
            </div>
          ) : (
            <CoverFlow grouped={visibleGrouped} onServiceClick={(tradeName, src) => openModal(tradeName, src)}/>
          )}
          <div className="text-center mt-5" data-aos="fade-up">
            <Link to="/services" className="kk-btn-gold" style={{borderRadius:12,padding:'14px 42px'}}>
              <i className="fas fa-th-list me-2"/>View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━ SEARCH RESULTS ━━━━━━━━━━ */}
      <div ref={resultsRef} style={{scrollMarginTop:'80px'}}/>
      {searched && (
        <section className="kk-results-section" aria-label="Search results" aria-live="polite">
          <div className="container">
            <div className="kk-results-bar">
              <span className="kk-results-tag">
                {loadingCraft ? 'Searching…'
                  : results.length > 0
                    ? <><strong>{results.length}</strong> provider{results.length!==1?'s':''} found for "<em>{query}</em>"</>
                    : <>No results for "{query}"</>
                }
              </span>
              <button className="kk-clear-btn" onClick={clearSearch}><i className="fas fa-times"/>Clear search</button>
            </div>
            {loadingCraft ? (
              <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-4">{[1,2,3].map(i=><SkeletonCard key={i}/>)}</div>
            ) : results.length === 0 ? (
              <div className="kk-empty">
                <span className="kk-empty-icon" role="img">🔍</span>
                <h5>No providers match your search</h5>
                <p>Try a broader term — e.g. "plumber" or "Nairobi".</p>
              </div>
            ) : (
              <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-4">
                {results.map((s,i)=><div key={s.id||i} className="col"><CraftsmanCard service={s}/></div>)}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ━━━━━━━━━━ ABOUT ━━━━━━━━━━ */}
      <section className="kk-about" id="about">
        <div className="container">
          <div className="text-center mb-5" data-aos="fade-up">
            <h2 className="kk-section-title">About KaaKazini</h2>
            <p className="kk-section-sub">Connecting skilled service providers with the clients who need them — across every county in Kenya.</p>
          </div>
          <div className="row align-items-center g-5">
            <div className="col-lg-6" data-aos="fade-right">
              <p style={{fontSize:'1.02rem',lineHeight:'1.9',color:'#4a5568',marginBottom:16}}><strong>KaaKazini</strong> is a Kenyan platform built to solve a real problem: clients struggle to find trusted service providers, and skilled professionals struggle to find reliable clients.</p>
              <p style={{fontSize:'1.02rem',lineHeight:'1.9',color:'#4a5568'}}>Every service provider is manually verified before they appear on the platform. You see their real portfolio, real reviews, and real contact details. From Nairobi to Kisumu — one skilled job at a time.</p>
            </div>
            <div className="col-lg-6" data-aos="fade-left">
              <div id="aboutCarousel" className="carousel slide kk-carousel-wrap" data-bs-ride="carousel" data-bs-interval="3500">
                <div className="carousel-inner h-100">
                  {[{src:'https://www.ariseiip.com/wp-content/uploads/2022/06/textile.png',alt:'Textile'},{src:c2,alt:'Service provider'},{src:c3,alt:'Carpentry'}].map((img,i)=>(
                    <div key={i} className={`carousel-item h-100 ${i===0?'active':''}`}><img src={img.src} className="kk-carousel-img" alt={img.alt} loading="lazy"/></div>
                  ))}
                </div>
                <button className="carousel-control-prev" type="button" data-bs-target="#aboutCarousel" data-bs-slide="prev"><span className="carousel-control-prev-icon"/></button>
                <button className="carousel-control-next" type="button" data-bs-target="#aboutCarousel" data-bs-slide="next"><span className="carousel-control-next-icon"/></button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━ HIRE ━━━━━━━━━━ */}
      <section className="kk-hire">
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-md-6" data-aos="fade-right">
              <h2 style={{fontFamily:"'Raleway',sans-serif",fontSize:'clamp(1.5rem,3.5vw,2.4rem)',fontWeight:800,color:'#000000',marginBottom:16}}>
                Hire Trusted Service Providers in Your County — Effortlessly
              </h2>
              <p style={{color:'var(--muted)',fontSize:'1rem',lineHeight:'1.85',marginBottom:20}}>Every service provider on KaaKazini is manually reviewed and approved before listing. Browse their portfolio, read real reviews, and hire directly.</p>
              <Link to="/craftsmen" className="kk-btn-gold" style={{borderRadius:11}}>
                <i className="fas fa-search me-2"/>Find a Service Provider
              </Link>
            </div>
            <div className="col-md-6" data-aos="fade-left">
              <div className="kk-hire-img-wrap">
                <img
                  src={pipersImage}
                  alt="Plumbers"
                  className="kk-hire-main"
                />
                <img src="https://www.wilsonmclain.com/wp-content/uploads/2013/03/2-resized.png" className="kk-hire-f1" alt="Provider tools" loading="lazy"/>
                <img src={c3} className="kk-hire-f2" alt="Carpentry work" loading="lazy"/>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━ TESTIMONIALS ━━━━━━━━━━ */}
      <section className="kk-testi">
        <div className="container">
          <div className="text-center mb-5" data-aos="fade-up">
            <h2 className="kk-section-title">What Our Clients Say</h2>
            <p className="kk-section-sub">Real feedback from people who found and hired service providers through KaaKazini</p>
          </div>
        </div>
        {reviewsLoading ? (
          <div className="text-center py-4"><div className="spinner-border text-success" role="status"><span className="visually-hidden">Loading…</span></div></div>
        ) : (
          <div className="kk-testi-track-wrap" style={{backgroundImage:`url(${bgImage})`,backgroundRepeat:'repeat',backgroundSize:'200px',padding:'28px 0'}}>
            <div className="kk-testi-track">
              {[...shownReviews,...shownReviews].map((r,i)=>(
                <div className="kk-testi-card" key={i}>
                  <div className="kk-testi-top">
                    <img src={FALLBACK_AVATARS[(r._i!==undefined?r._i:i%shownReviews.length)%3]} alt={r.reviewer||'Client'} className="kk-testi-avatar" loading="lazy"/>
                    <div>
                      <p className="kk-testi-name">{r.reviewer||'Anonymous'}</p>
                      {r.location&&<p className="kk-testi-loc">{r.location}</p>}
                      <StarRating rating={Number(r.rating)}/>
                    </div>
                  </div>
                  <p className="kk-testi-comment">"{r.comment}"</p>
                  {(r.craftsman_name||r.trade)&&(
                    <div style={{display:'flex',alignItems:'center',gap:9,marginTop:10,borderTop:'1px solid #f0f0f0',paddingTop:10}}>
                      {r.craftsman_name&&<img src={r.craftsman_image?imgUrl(r.craftsman_image):`https://ui-avatars.com/api/?name=${encodeURIComponent(r.craftsman_name)}&background=fff000&color=000000&size=64`} alt={r.craftsman_name} style={{width:32,height:32,borderRadius:'50%',objectFit:'cover',border:'2px solid var(--gold)',flexShrink:0}} loading="lazy" onError={e=>{ e.target.src=`https://ui-avatars.com/api/?name=${encodeURIComponent(r.craftsman_name)}&background=fff000&color=000000&size=64`; }}/>}
                      <div>
                        {r.craftsman_name&&<p style={{fontSize:'.72rem',color:'var(--green)',fontWeight:700,margin:0}}>{r.craftsman_name}</p>}
                        {r.trade&&<p style={{fontSize:'.68rem',color:'var(--muted)',margin:0}}>{r.trade}</p>}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* ━━━━━━━━━━ FAQ ━━━━━━━━━━ */}
      <section className="kk-faq" id="faq">
        <div className="container">
          <div className="text-center mb-5" data-aos="fade-up">
            <h2 className="kk-section-title">Frequently Asked Questions</h2>
          </div>
          <div className="row align-items-start g-5">
            <div className="col-lg-6" data-aos="fade-right">
              <div className="accordion" id="faqAcc">
                {[
                  {q:'How do I hire a service provider?',a:"Search by service and county, click a card to view their profile, then click 'Hire Now'."},
                  {q:'Is it free to search and hire?',a:'Yes — searching and contacting service providers is completely free for clients.'},
                  {q:'Are the service providers verified?',a:'Yes. Every service provider is manually reviewed by our team before they appear on the platform.'},
                  {q:'What services are available?',a:'Plumbing, electrical, carpentry, tiling, masonry, tailoring, metalwork, painting, and more.'},
                  {q:'How do I leave a review?',a:"After your job is done, visit the provider's profile and submit a rating and review."},
                  {q:'Are service providers available in my county?',a:'We have service providers across 40+ counties in Kenya. Use the county filter when searching.'},
                ].map((f,i)=>(
                  <div className="accordion-item kk-faq-item" key={i} data-aos="fade-up" data-aos-delay={i*50}>
                    <h2 className="accordion-header" id={`fh${i}`}>
                      <button className={`accordion-button kk-faq-btn ${i!==0?'collapsed':''}`} type="button" data-bs-toggle="collapse" data-bs-target={`#fc${i}`} aria-expanded={i===0?'true':'false'}>{f.q}</button>
                    </h2>
                    <div id={`fc${i}`} className={`accordion-collapse collapse ${i===0?'show':''}`} aria-labelledby={`fh${i}`} data-bs-parent="#faqAcc">
                      <div className="kk-faq-body">{f.a}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── "Still have a question?" form is currently switched off.
                To bring it back, add a second column here:
                <div className="col-lg-6" data-aos="fade-left">
                  <RaiseIssueForm jobs={[]} isAuthenticated={isLoggedIn} />
                </div>
            */}
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━ FOOTER ━━━━━━━━━━ */}
      <footer className="kk-footer">
        <div className="container">
          <div className="row g-5">
            <div className="col-lg-3 col-md-6">
              <h5>Quick Links</h5>
              <ul className="list-unstyled" style={{lineHeight:'2.2'}}>
                <li><Link to="/">Home</Link></li>
                <li><Link to="/craftsmen">Find a Service Provider</Link></li>
                <li><Link to="/services">All Services</Link></li>
                {!isCraftsman&&<li><Link to="/signup">Become a Service Provider</Link></li>}
                {isCraftsman &&<li><Link to="/craftsman-dashboard">My Dashboard</Link></li>}
                {isClient    &&<li><Link to="/hire">My Hire Dashboard</Link></li>}
                <li><a href="#faq">FAQ</a></li>
              </ul>
            </div>
            <div className="col-lg-4 col-md-6">
              <h5>Contact Us</h5>
              <p><i className="fas fa-map-marker-alt me-2" style={{color:'var(--gold)'}}/>Kisumu, Kenya</p>
              <p><i className="fas fa-envelope me-2" style={{color:'var(--gold)'}}/>support@kaakazini.com</p>
              <div className="kk-social mt-3">
                <h5>Follow Us</h5>
                {[['fab fa-facebook-f','Facebook'],['fab fa-twitter','Twitter'],['fab fa-instagram','Instagram'],['fab fa-linkedin-in','LinkedIn']].map(([icon,lbl])=>(
                  <a key={lbl} href="#" aria-label={lbl}><i className={icon}/></a>
                ))}
              </div>
            </div>
            <div className="col-lg-5">
              <h5>Find Us</h5>
              <div className="kk-map-wrap">
                <iframe title="KaaKazini — Kisumu" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d63828.69947405925!2d34.7106301!3d-0.1022054!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x182aa5b2e0a70b83%3A0x36005f520589fdfc!2sKisumu!5e0!3m2!1sen!2ske!4v1718888888888!5m2!1sen!2ske"
                  width="100%" height="220" style={{border:0,display:'block'}} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade"/>
              </div>
            </div>
          </div>
          <hr className="kk-footer-hr"/>
          <div className="kk-footer-bottom">
            <p style={{margin:0}}>© {new Date().getFullYear()} <strong style={{color:'#fff'}}>KaaKazini</strong>. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <button className={`kk-back-top ${showTop?'on':''}`} onClick={()=>window.scrollTo({top:0,behavior:'smooth'})} aria-label="Back to top">
        <i className="fas fa-arrow-up"/>
      </button>
    </>
  );
}